import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { captureEvent } from "@/lib/analyticsQueue";
import { toast } from "@/lib/toast";
import { useLanguage } from "@/contexts/LanguageContext";
import { useCredits } from "@/contexts/CreditsContext";
import { useCreditCosts } from "@/hooks/useCreditCosts";
import { useGeneratedVideos } from "@/hooks/useGeneratedVideos";
import { useDbBackedPreviewPair } from "@/hooks/useDbBackedPreviewPair";
import { useLightboxNavigation } from "@/hooks/useLightboxNavigation";
import { usePersistentPendingBoost } from "@/hooks/usePersistentPendingBoost";
import { apiPost } from "@/lib/api/apiClient";
import { checkGenerationStatus } from "@/lib/api/generateVideo";
import { uploadImagesToR2 } from "@/lib/api/uploadImage";
import { useImageUpload } from "@/hooks/useImageUpload";
import { useAnonymousGate } from "@/hooks/useAnonymousGate";
import {
  clearFlowState,
  useFlowPersistence,
} from "@/hooks/useFlowPersistence";
import { FLOW_KEYS } from "@/config/flowKeys";
import { downloadMedia, shareMedia } from "@/lib/mediaUtils";
import { videoToDisplayMedia } from "@/lib/types/media";
import type { DisplayMedia } from "@/lib/types/media";
import { ANALYTICS_EVENTS, FLOW_IDS } from "@shared/schemas/analytics";
import { captureGenerationError } from "@/lib/sentry";
import type {
  BaseballTrendAspectRatio,
  BaseballTrendDuration,
  BaseballTrendPresetId,
  PreviewBaseballTrendRequest,
  PreviewBaseballTrendResponse,
  RenderBaseballTrendRequest,
  RenderBaseballTrendResponse,
} from "@shared/schemas/api";
import type { BaseballTrendActionMode } from "@/components/flowly/BaseballTrend/BaseballTrendActionPicker";
import type { BaseballTrendWardrobeMode } from "@/components/flowly/BaseballTrend/BaseballTrendWardrobePicker";
import {
  BASEBALL_TREND_DEFAULT_ASPECT_RATIO,
  BASEBALL_TREND_DEFAULT_DURATION,
  BASEBALL_TREND_DEFAULT_IMAGE_MODEL_ID,
  BASEBALL_TREND_IMAGE_RESOLUTION,
  BASEBALL_TREND_PREVIEW_COUNT,
  BASEBALL_TREND_RENDER_MODEL_ID,
  BASEBALL_TREND_RENDER_RESOLUTION,
  type BaseballTrendImageModelId,
} from "@/config/baseballTrend";
import {
  BASEBALL_TREND_CONFIG_ANCHOR_ID,
  BASEBALL_TREND_PRESETS_ANCHOR_ID,
  BASEBALL_TREND_WARDROBE_ANCHOR_ID,
  BASEBALL_TREND_ACTION_ANCHOR_ID,
} from "./constants";
import type { UploadedSource } from "./types";

const PIPELINE = "baseball_trend";

let uploadIdCounter = 0;
const nextUploadId = () => `bt-upload-${Date.now()}-${++uploadIdCounter}`;

function scrollToAnchor(id: string): void {
  requestAnimationFrame(() => {
    document
      .getElementById(id)
      ?.scrollIntoView({ behavior: "smooth", block: "center" });
  });
}

export function useBaseballTrend() {
  const { t } = useLanguage();
  const { normalizeOrWarn } = useImageUpload();
  const { refreshCredits, handleCreditError } = useCredits();

  // ── Step state ──
  const [upload, setUpload] = useState<UploadedSource | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [presetId, setPresetId] = useState<BaseballTrendPresetId | null>(null);
  const [wardrobeMode, setWardrobeMode] = useState<BaseballTrendWardrobeMode>("default");
  const [customWardrobe, setCustomWardrobe] = useState("");
  // Preview-pair state (selection, optimistic flag, dismiss mark) + the
  // DB-backed batch derivation live in the shared hook below.
  const [actionMode, setActionMode] = useState<BaseballTrendActionMode>("default");
  const [customAction, setCustomAction] = useState("");
  const [duration, setDuration] = useState<BaseballTrendDuration>(
    BASEBALL_TREND_DEFAULT_DURATION,
  );
  const [aspectRatio, setAspectRatio] = useState<BaseballTrendAspectRatio>(
    BASEBALL_TREND_DEFAULT_ASPECT_RATIO,
  );
  const [imageModelId, setImageModelId] = useState<BaseballTrendImageModelId>(
    BASEBALL_TREND_DEFAULT_IMAGE_MODEL_ID,
  );
  const [isRendering, setIsRendering] = useState(false);
  const [selectedMedia, setSelectedMedia] = useState<DisplayMedia | null>(null);
  const firstUploadTipShownRef = useRef(false);

  useEffect(() => {
    captureEvent(ANALYTICS_EVENTS.TOOL_PAGE_VIEW, {
      flow: FLOW_IDS.BASEBALL_TREND,
    });
  }, []);

  // Preview pair — DB-backed, realtime-synced, refresh/auth/top-up resilient.
  // The shared hook owns the batch derivation, the dismiss high-water mark,
  // the optimistic bridge and selection-by-stable-id (same mechanism Talking
  // Avatar uses). Destructured with the original names so the rest of this
  // hook + the page component are untouched.
  const {
    previewUrls,
    selectedPreviewIndex,
    selectedHistoricalUrl,
    isPreviewing,
    previewStartedAt,
    pastPreviewImages,
    effectivePreviewUrl,
    hasPreviewSelection,
    selectByIndex: selectPreviewByIndex,
    selectHistorical: selectHistoricalPreview,
    clearPreviews,
    beginPreview,
    endPreview,
    persisted: previewPersisted,
    restorePersisted: restorePreviewPersisted,
  } = useDbBackedPreviewPair({ pipeline: PIPELINE });

  // Persist the in-progress configuration, the uploaded photo AND any
  // completed preview pair so the auth/credit wall doesn't wipe progress.
  // `upload` is an image key → its File bytes survive in IndexedDB (no longer
  // dependent on a post-generate R2 URL, which an anon user never has).
  // The preview pair itself is NOT persisted here — it's server state
  // (generated_images rows) rebuilt from the realtime store on every mount,
  // so it survives refresh/redirect/auth/top-up for free. We only persist
  // the user's PICK (stable image id / history url) across the auth wall.
  useFlowPersistence({
    key: FLOW_KEYS.BASEBALL_TREND,
    state: {
      upload,
      presetId,
      aspectRatio,
      wardrobeMode,
      customWardrobe,
      imageModelId,
      ...previewPersisted,
    },
    imageKeys: ["upload"],
    onRestore: (s) => {
      if (s.upload && typeof s.upload === "object") {
        setUpload(s.upload as UploadedSource);
      }
      if (s.presetId) setPresetId(s.presetId as BaseballTrendPresetId);
      if (s.aspectRatio === "16:9" || s.aspectRatio === "9:16") setAspectRatio(s.aspectRatio);
      if (s.wardrobeMode === "default" || s.wardrobeMode === "custom") setWardrobeMode(s.wardrobeMode);
      if (typeof s.customWardrobe === "string") setCustomWardrobe(s.customWardrobe);
      if (typeof s.imageModelId === "string") setImageModelId(s.imageModelId as BaseballTrendImageModelId);
      restorePreviewPersisted(s);
    },
  });

  // ── Gallery ──
  const {
    completedVideos,
    pendingVideos: dbPendingVideos,
    completingIds: videoCompletingIds,
    loading: videosLoading,
  } = useGeneratedVideos(PIPELINE);

  /** Survives full-page refreshes so the skeleton doesn't blink. */
  const { pendingCount, increment: incrementPending, decrement: decrementPending } =
    usePersistentPendingBoost({
      pipeline: PIPELINE,
      dbPendingCount: dbPendingVideos.length,
      isDbLoading: videosLoading,
    });

  // Poll status for any pending baseball-trend video until terminal.
  const pendingIdsKey = dbPendingVideos
    .map((v) => v.id)
    .sort()
    .join(",");
  const pendingIdsRef = useRef<string[]>([]);
  pendingIdsRef.current = dbPendingVideos.map((v) => v.id);
  const inFlightCheckRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (!pendingIdsKey) return;
    const tick = () => {
      for (const id of pendingIdsRef.current) {
        if (inFlightCheckRef.current.has(id)) continue;
        inFlightCheckRef.current.add(id);
        void checkGenerationStatus(id, "video").finally(() => {
          inFlightCheckRef.current.delete(id);
        });
      }
    };
    tick();
    const intervalId = window.setInterval(tick, 20_000);
    return () => window.clearInterval(intervalId);
  }, [pendingIdsKey]);

  const galleryVideos: DisplayMedia[] = useMemo(
    () => completedVideos.filter((v) => v.video_url).map(videoToDisplayMedia),
    [completedVideos],
  );

  const { currentMediaIndex, canNavigate, goToNextItem, goToPrevItem } =
    useLightboxNavigation(galleryVideos, selectedMedia, setSelectedMedia);

  // ── Step 1: Upload ──
  // Local-only: keep the File in memory with a blob preview. The R2 push
  // happens lazily inside `ensureUploadedToR2`, called by the preview/render
  // handlers AFTER the anonymous gate. This lets signed-out visitors attach
  // a photo and configure the flow before being asked to sign up.
  const handleAddFile = useCallback(
    async (rawFile: File) => {
      const file = await normalizeOrWarn(rawFile);
      if (!file) return;
      const wasEmpty = !upload;
      if (upload) URL.revokeObjectURL(upload.previewUrl);
      setUpload({
        id: nextUploadId(),
        publicUrl: "",
        previewUrl: URL.createObjectURL(file),
        file,
      });
      clearPreviews();
      if (wasEmpty) {
        scrollToAnchor(BASEBALL_TREND_CONFIG_ANCHOR_ID);
        if (!firstUploadTipShownRef.current) {
          firstUploadTipShownRef.current = true;
          toast.info(t.baseballTrend.uploadTitle, {
            description: t.baseballTrend.uploadHint,
            duration: 6000,
            position: "top-center",
            closeButton: true,
          });
        }
      }
    },
    [upload, t, clearPreviews, normalizeOrWarn],
  );

  const handleRemoveUpload = useCallback(() => {
    if (upload) URL.revokeObjectURL(upload.previewUrl);
    setUpload(null);
    setPresetId(null);
    clearPreviews();
    // User wiped their photo — drop any persisted draft so the next mount
    // starts blank rather than re-hydrating the just-removed selection.
    clearFlowState(FLOW_KEYS.BASEBALL_TREND);
  }, [upload, clearPreviews]);

  /** Lazy-upload the local File to R2 the first time we need a CDN URL.
   *  Caches the resulting publicUrl on the upload state so a follow-up
   *  preview/render reuses the same bytes. Throws on failure. */
  const ensureUploadedToR2 = useCallback(async (): Promise<string> => {
    if (!upload) throw new Error("No image to upload");
    if (upload.publicUrl) return upload.publicUrl;
    if (!upload.file) throw new Error("Missing local file for upload");
    setIsUploading(true);
    try {
      const [uploaded] = await uploadImagesToR2([upload.file]);
      if (!uploaded) throw new Error("Upload failed");
      setUpload((prev) =>
        prev && prev.id === upload.id
          ? { ...prev, publicUrl: uploaded.publicUrl }
          : prev,
      );
      return uploaded.publicUrl;
    } finally {
      setIsUploading(false);
    }
  }, [upload]);

  // ── Step 2: Preset ──
  const handlePresetSelect = useCallback(
    (id: BaseballTrendPresetId) => {
      setPresetId(id);
      captureEvent(ANALYTICS_EVENTS.TEMPLATE_SELECTED, {
        flow: FLOW_IDS.BASEBALL_TREND,
        preset_id: id,
      });
      clearPreviews();
      scrollToAnchor(BASEBALL_TREND_WARDROBE_ANCHOR_ID);
    },
    [clearPreviews],
  );

  // ── Step 3: Wardrobe ──
  const handleWardrobeModeChange = useCallback(
    (next: BaseballTrendWardrobeMode) => {
      setWardrobeMode(next);
      clearPreviews();
    },
    [clearPreviews],
  );

  const handleCustomWardrobeChange = useCallback((value: string) => {
    setCustomWardrobe(value);
    // Don't clear previews on every keystroke — only when the user actually
    // changes mode or hits "Generate". Otherwise typing trashes a good pair.
  }, []);

  // ── Step 4: Preview ──
  const canGeneratePreview =
    !!upload && !!presetId && !isPreviewing && !isUploading;

  const generatePreviewImpl = useCallback(async () => {
    if (!canGeneratePreview || !upload || !presetId) {
      if (!upload) toast.error(t.baseballTrend.missingUpload);
      else if (!presetId) {
        toast.error(t.baseballTrend.missingPreset);
        scrollToAnchor(BASEBALL_TREND_PRESETS_ANCHOR_ID);
      }
      return;
    }
    // Optimistic bridge only — the durable in-flight state is the pending
    // generated_images batch this request creates server-side.
    beginPreview();
    try {
      // Lazy R2 upload: anonymous browsing kept the File local, so the very
      // first preview after signup is the moment we actually push bytes.
      const sourceImageUrl = await ensureUploadedToR2();
      const trimmedWardrobe = customWardrobe.trim();
      const body: PreviewBaseballTrendRequest = {
        presetId,
        sourceImageUrl,
        aspectRatio,
        imageModelId,
        ...(wardrobeMode === "custom" && trimmedWardrobe.length > 0
          ? { customWardrobe: trimmedWardrobe }
          : {}),
      };
      // Blocking endpoint: by the time it resolves the server has already
      // written the completed generated_images rows; the realtime store
      // surfaces them as the current batch. We intentionally ignore the
      // response body — the DB is the single source of truth.
      await apiPost<PreviewBaseballTrendResponse>(
        "/api/baseball-trend/preview",
        body,
      );
    } catch (err) {
      captureGenerationError(err, {
        flow: FLOW_IDS.BASEBALL_TREND,
        action: "preview",
        endpoint: "/api/baseball-trend/preview",
      });
      // Parity with old flows: insufficient credits opens the pricing modal;
      // any other failure shows the toast. The server-side refund is
      // reflected via refreshCredits() in `finally` (success OR failure).
      if (!handleCreditError(err)) {
        toast.error(t.baseballTrend.generationFailed, {
          description:
            err instanceof Error ? err.message : t.baseballTrend.generationFailedDesc,
        });
      }
    } finally {
      // Endpoint is synchronous through completion, so by here the batch is
      // already terminal in the DB; hand the in-flight signal back to it.
      endPreview();
      await refreshCredits();
    }
  }, [
    canGeneratePreview,
    upload,
    presetId,
    aspectRatio,
    imageModelId,
    wardrobeMode,
    customWardrobe,
    ensureUploadedToR2,
    refreshCredits,
    handleCreditError,
    beginPreview,
    endPreview,
    t,
  ]);
  const handleGeneratePreview = useAnonymousGate(generatePreviewImpl);

  const handlePreviewSelect = useCallback(
    (idx: number) => {
      selectPreviewByIndex(idx);
      scrollToAnchor(BASEBALL_TREND_ACTION_ANCHOR_ID);
    },
    [selectPreviewByIndex],
  );

  /** User picked a preview from their history (NOT the current pair). */
  const handleHistoricalPreviewSelect = useCallback(
    (url: string) => {
      selectHistoricalPreview(url);
      scrollToAnchor(BASEBALL_TREND_ACTION_ANCHOR_ID);
    },
    [selectHistoricalPreview],
  );

  // ── Step 5: Motion ──
  const handleActionModeChange = useCallback((next: BaseballTrendActionMode) => {
    setActionMode(next);
  }, []);

  const handleCustomActionChange = useCallback((value: string) => {
    setCustomAction(value);
  }, []);

  // ── Step 6 controls: duration / aspect ratio ──
  const handleDurationChange = useCallback((next: BaseballTrendDuration) => {
    setDuration(next);
  }, []);

  const handleAspectRatioChange = useCallback(
    (next: BaseballTrendAspectRatio) => {
      setAspectRatio(next);
      // The aspect-ratio changes the framing of the preview itself, so the
      // existing pair (if any) is stale.
      clearPreviews();
    },
    [clearPreviews],
  );

  const handleImageModelChange = useCallback(
    (next: BaseballTrendImageModelId) => {
      setImageModelId(next);
      // A different image model produces a different look, so the preview
      // pair (if any) doesn't match anymore — clear so the user regenerates.
      clearPreviews();
    },
    [clearPreviews],
  );

  // ── Lightbox callbacks ──
  const handleOpenLightbox = useCallback((video: DisplayMedia) => {
    setSelectedMedia(video);
  }, []);

  const handleCloseLightbox = useCallback(() => {
    setSelectedMedia(null);
  }, []);

  /** Restore the source image from the selected gallery video back into the
   *  upload slot, clear previews, close the lightbox and scroll to top so the
   *  user can tweak settings and re-render. Mirrors the regenerate pattern in
   *  TextToVideo / FramesToVideo / ImageGenerator. */
  const handleRegenerate = useCallback(() => {
    const sourceUrl = selectedMedia?.referenceImageUrls[0];
    if (sourceUrl) {
      setUpload({
        id: nextUploadId(),
        publicUrl: sourceUrl,
        previewUrl: sourceUrl,
      });
    }
    clearPreviews();
    setSelectedMedia(null);
    setTimeout(() => window.scrollTo({ top: 0, behavior: "smooth" }), 0);
  }, [selectedMedia, clearPreviews]);

  const handleDownload = useCallback(async () => {
    if (!selectedMedia?.url) return;
    await downloadMedia(selectedMedia.url, "baseball-trend", "mp4");
  }, [selectedMedia]);

  const handleShare = useCallback(async () => {
    if (!selectedMedia?.id) return;
    await shareMedia({ id: selectedMedia.id, type: selectedMedia.type });
  }, [selectedMedia]);

  // ── Derived ──

  // Live credit costs — the exact `credit_costs` rows the server charges from
  // (no hand-mirrored prices). Same pattern as every other flow's hook
  // (see useTalkingAvatar.ts / useTextToVideo.ts).
  const { getImageGenerateCost, getVideoCost } = useCreditCosts();

  /** Cost of just the next preview-pair generation (Stage 1) — used on the
   *  "Generate previews" chip so the user sees the impact of switching models. */
  const previewCost = useMemo(
    () =>
      getImageGenerateCost(BASEBALL_TREND_IMAGE_RESOLUTION, imageModelId) *
      BASEBALL_TREND_PREVIEW_COUNT,
    [getImageGenerateCost, imageModelId],
  );

  /** Cost of just the next render (Stage 2) — used when previews are already
   *  picked, so the chip reflects what the next click actually charges.
   *  Stage 2 has no sound toggle, so no `_audio` variant. */
  const renderCost = useMemo(
    () =>
      getVideoCost(
        duration,
        BASEBALL_TREND_RENDER_RESOLUTION,
        BASEBALL_TREND_RENDER_MODEL_ID,
      ),
    [getVideoCost, duration],
  );

  const canGenerate =
    !!upload &&
    !!presetId &&
    hasPreviewSelection &&
    !isPreviewing &&
    !isRendering &&
    !isUploading;

  // ── Final render (Stage 2) ──
  const handleGenerate = useCallback(async () => {
    if (
      !canGenerate ||
      !upload ||
      !presetId ||
      !hasPreviewSelection
    ) {
      if (!upload) toast.error(t.baseballTrend.missingUpload);
      else if (!presetId) toast.error(t.baseballTrend.missingPreset);
      else if (!hasPreviewSelection) toast.error(t.baseballTrend.missingPreview);
      return;
    }
    const selectedPreviewUrl = effectivePreviewUrl;
    if (!selectedPreviewUrl) return;

    setIsRendering(true);
    // Optimistic: paint the pending video card now, before the upload/network
    // round-trip. The DB row + Realtime update takes over a beat later and the
    // boost auto-zeroes (usePersistentPendingBoost). Survives page refresh.
    incrementPending(1);
    try {
      const trimmedAction = customAction.trim();
      const body: RenderBaseballTrendRequest = {
        presetId,
        selectedPreviewUrl,
        duration,
        aspectRatio,
        ...(actionMode === "custom" && trimmedAction.length > 0
          ? { customAction: trimmedAction }
          : {}),
      };
      await apiPost<RenderBaseballTrendResponse>("/api/baseball-trend/render", body);
      // Final render kicked off — drop the persisted draft AND reset inputs
      // to defaults so a fresh visit (and the on-screen form) doesn't replay
      // this exact configuration/photo. The video shows in the DB-backed
      // gallery, untouched here.
      clearFlowState(FLOW_KEYS.BASEBALL_TREND);
      if (upload) URL.revokeObjectURL(upload.previewUrl);
      setUpload(null);
      setPresetId(null);
      setWardrobeMode("default");
      setCustomWardrobe("");
      clearPreviews();
      setActionMode("default");
      setCustomAction("");
      setDuration(BASEBALL_TREND_DEFAULT_DURATION);
      setAspectRatio(BASEBALL_TREND_DEFAULT_ASPECT_RATIO);
      setImageModelId(BASEBALL_TREND_DEFAULT_IMAGE_MODEL_ID);
    } catch (err) {
      // Failed before a DB row exists — remove the optimistic card.
      decrementPending(1);
      captureGenerationError(err, {
        flow: FLOW_IDS.BASEBALL_TREND,
        action: "render",
        endpoint: "/api/baseball-trend/render",
      });
      if (!handleCreditError(err)) {
        toast.error(t.baseballTrend.generationFailed, {
          description:
            err instanceof Error ? err.message : t.baseballTrend.generationFailedDesc,
        });
      }
    } finally {
      setIsRendering(false);
      await refreshCredits();
    }
  }, [
    canGenerate,
    upload,
    presetId,
    hasPreviewSelection,
    effectivePreviewUrl,
    duration,
    aspectRatio,
    actionMode,
    customAction,
    clearPreviews,
    refreshCredits,
    handleCreditError,
    incrementPending,
    decrementPending,
    t,
  ]);

  return {
    // Step state
    upload,
    isUploading,
    presetId,
    wardrobeMode,
    customWardrobe,
    previewUrls,
    selectedPreviewIndex,
    selectedHistoricalUrl,
    pastPreviewImages,
    isPreviewing,
    previewStartedAt,
    actionMode,
    customAction,
    duration,
    aspectRatio,
    isGenerating: isRendering,

    // Gallery
    galleryVideos,
    dbPendingVideos,
    videoCompletingIds,
    pendingCount,
    selectedMedia,

    // Handlers
    handleAddFile,
    handleRemoveUpload,
    handlePresetSelect,
    handleWardrobeModeChange,
    handleCustomWardrobeChange,
    handleGeneratePreview,
    handlePreviewSelect,
    handleHistoricalPreviewSelect,
    handleActionModeChange,
    handleCustomActionChange,
    handleDurationChange,
    handleAspectRatioChange,
    handleGenerate,
    handleOpenLightbox,
    handleCloseLightbox,
    handleRegenerate,
    handleDownload,
    handleShare,

    // Lightbox navigation
    currentMediaIndex,
    canNavigate,
    goToNextItem,
    goToPrevItem,

    // Derived
    canGenerate,
    canGeneratePreview,
    renderCost,

    // Constants surfaced for the UI
    previewSlots: BASEBALL_TREND_PREVIEW_COUNT,
    previewCost,

    // Image-model picker (lives in the bottom bar)
    imageModelId,
    handleImageModelChange,

    // i18n
    t,
  };
}
